import { css } from '@emotion/react';

import { breakpoints } from '@coursera/cds-common';

import generateEmotionClasses from '@core/utils/generateEmotionClasses';

import { getPaletteColor } from './colors';
import type { DialogColor, DialogWidth, SlotOrientation } from './types';

export const classes = generateEmotionClasses('Dialog', [
  'dialog',
  'contentWrapper',
  'slot',
]);

const getDialogWidthCss = (width: DialogWidth) => {
  if (width === 'small') {
    return css`
      width: 340px;
      max-height: 95vh;
    `;
  }

  if (width === 'medium') {
    return css`
      width: 568px;
    `;
  }

  return css`
    width: 60vw;

    ${breakpoints.only('md')} {
      width: 70vw;
    }

    ${breakpoints.only('sm')} {
      width: 568px;
    }
  `;
};

/**
 * Base grid layout with close button in grid.
 * Used directly on dialog when no slot is present.
 */
const baseGridWithCloseCss = css`
  display: grid;
  grid-template:
    'headingGroup close' auto
    '. .' auto
    'content content' 1fr
    'actions actions' auto
    / 1fr auto;
  overflow-y: auto;
`;

/**
 * Grid layout with close button for content wrapper inside flex container.
 * Includes flex properties needed when inside a slot layout.
 */
const contentGridWithCloseCss = css`
  ${baseGridWithCloseCss}
  flex: 1;
  min-width: 0;
`;

/**
 * Grid layout without close button (close is absolutely positioned).
 * Heading group spans full width. Used for content wrapper inside flex container.
 */
const contentGridWithoutCloseCss = css`
  display: grid;
  grid-template:
    'headingGroup' auto
    '.' auto
    'content' 1fr
    'actions' auto
    / 1fr;
  flex: 1;
  min-width: 0;
  overflow-y: auto;
`;

/**
 * Get the flex direction based on slot orientation.
 * - horizontalStart = row (slot on left)
 * - horizontalEnd = row-reverse (slot on right)
 * - verticalTop = column (slot on top)
 */
const getFlexDirection = (slotOrientation: SlotOrientation) => {
  switch (slotOrientation) {
    case 'horizontalStart':
      return 'row';
    case 'horizontalEnd':
      return 'row-reverse';
    case 'verticalTop':
      return 'column';
    default:
      return 'row';
  }
};

/**
 * Returns CSS for the content wrapper.
 * @param options.isCloseAbsolute - When true, uses grid without close area (heading spans full width)
 * @param options.color - When provided (slot is present), applies background color to entire content area
 * @param options.slotOrientation - The orientation of the slot
 * @param options.hasSlot - Whether a slot is present
 */
export const getContentWrapperCss = ({
  isCloseAbsolute = false,
  color,
  slotOrientation,
  hasSlot = false,
}: {
  isCloseAbsolute?: boolean;
  color?: DialogColor;
  slotOrientation?: SlotOrientation;
  hasSlot?: boolean;
} = {}) => {
  const gridCss = isCloseAbsolute
    ? contentGridWithoutCloseCss
    : contentGridWithCloseCss;

  const isSlotOnTop = hasSlot && slotOrientation === 'verticalTop';

  // When slot is on top (vertical), let dialog handle scroll instead of contentWrapper
  // flex: 1 0 auto = grow to fill space, don't shrink below content, basis auto
  // This ensures actions stay at bottom while allowing content to exceed dialog height
  const slotOnTopOverrides = isSlotOnTop
    ? css`
        flex: 1 0 auto;
        overflow-y: unset;
      `
    : null;

  // For horizontal slots, apply same overrides at xs breakpoint (when slot moves to top)
  const isHorizontalSlot =
    hasSlot &&
    (slotOrientation === 'horizontalStart' ||
      slotOrientation === 'horizontalEnd');
  const mobileOverrides = isHorizontalSlot
    ? css`
        ${breakpoints.down('xs')} {
          flex: 1 0 auto;
          overflow-y: unset;
        }
      `
    : null;

  // When slot is present, apply color to entire content wrapper
  const colorCss = color
    ? css`
        background-color: ${getPaletteColor(color)};
      `
    : null;

  return css`
    ${gridCss}
    ${colorCss}
    ${slotOnTopOverrides}
    ${mobileOverrides}
  `;
};

/**
 * Returns CSS for the slot container.
 * - horizontalStart/horizontalEnd: max-width 50%, height stretches to fill container
 * - verticalTop: max-width 100%, full width
 * - xs breakpoint: always full width (stacked layout)
 * - color: applies background color to slot when provided (for transparent SVG slots)
 *
 * Note: !important is used at xs to override any inline styles from slotProps,
 * ensuring the slot always becomes full width on mobile.
 */
export const getSlotCss = (
  slotOrientation: SlotOrientation,
  color?: DialogColor
) => {
  const isVertical = slotOrientation === 'verticalTop';

  return css`
    display: flex;
    overflow: hidden;
    flex-shrink: 0;
    align-self: stretch;
    max-width: ${isVertical ? '100%' : '50%'};
    ${color && `background-color: ${getPaletteColor(color)};`}

    ${breakpoints.down('xs')} {
      max-width: 100%;
      width: 100%;
    }
  `;
};

/**
 * Returns CSS for the dialog, applied via the css prop on BaseDialog.
 * Uses nested selectors to target the dialog element and content wrapper.
 */
export const getDialogCss = ({
  width,
  hasSlot = false,
  slotOrientation,
}: {
  width: DialogWidth;
  hasSlot?: boolean;
  slotOrientation?: SlotOrientation;
}) => {
  const baseDialogStyles = css`
    background-color: var(--cds-color-neutral-background-primary);
    border-radius: var(--cds-border-radius-200);
    max-height: 80vh;
    overflow-y: auto;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%);

    ${getDialogWidthCss(width)}

    ${breakpoints.down('xs')} {
      border-radius: 0;
      max-height: unset;
      height: 100%;
      width: 100%;
    }
  `;

  if (!hasSlot || !slotOrientation) {
    return css`
      .${classes.dialog} {
        ${baseDialogStyles}
        ${baseGridWithCloseCss}
      }
    `;
  }

  return css`
    .${classes.dialog} {
      ${baseDialogStyles}
      display: flex;
      flex-direction: ${getFlexDirection(slotOrientation)};

      ${breakpoints.down('xs')} {
        flex-direction: column;
      }
    }
  `;
};
